import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Play, Square, MessageSquare, X, Lock, AlertTriangle,
  ArrowUp, ArrowDown, Eye, Edit3, Download, Star, RefreshCw, Users, FileText, ArrowLeft,
} from 'lucide-react';
import { feedbackAdminAPI } from '../lib/api';
import { PageHeader, LoadingPage, StatCard, EmptyState, Alert, FormGroup } from '../components/shared';
import toast from 'react-hot-toast';

const LIKERT = { 1: 'Strongly Disagree', 2: 'Disagree', 3: 'Neutral', 4: 'Agree', 5: 'Strongly Agree' };
const LIKERT_COLOR = { 1: '#ef4444', 2: '#f97316', 3: '#eab308', 4: '#22c55e', 5: 'var(--teal)' };

// ── Question builder ────────────────────────────────────────
const QuestionBuilder = ({ questions, setQuestions, responseCount }) => {
  const add = () => setQuestions([...questions, {
    id: null, question_text: '', question_type: 'rating', render_style: 'stars',
    options: null, is_required: true, answer_count: 0,
  }]);

  const update = (i, field, val) => {
    const next = [...questions];
    next[i] = { ...next[i], [field]: val };
    if (field === 'question_type') {
      next[i].render_style = 'stars';
      if (val === 'multiple_choice' && !next[i].options) next[i].options = [''];
    }
    setQuestions(next);
  };

  const remove = (i) => {
    const q = questions[i];
    if (q.answer_count > 0 &&
      !window.confirm(`This will permanently delete ${q.answer_count} response${q.answer_count !== 1 ? 's' : ''} to this question. Continue?`)) return;
    setQuestions(questions.filter((_, x) => x !== i));
  };

  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= questions.length) return;
    const next = [...questions]; [next[i], next[j]] = [next[j], next[i]]; setQuestions(next);
  };

  const opts = (q) => (typeof q.options === 'string' ? JSON.parse(q.options) : q.options) || [];
  const addOpt = (i) => { const n = [...questions]; n[i].options = [...opts(n[i]), '']; setQuestions(n); };
  const setOpt = (i, oi, v) => { const n = [...questions]; const o = [...opts(n[i])]; o[oi] = v; n[i].options = o; setQuestions(n); };
  const rmOpt = (i, oi) => {
    const q = questions[i];
    if (q.answer_count > 0 && oi < opts(q).length) {
      toast.error('Existing options cannot be removed — this question has responses.');
      return;
    }
    const n = [...questions]; n[i].options = opts(n[i]).filter((_, x) => x !== oi); setQuestions(n);
  };

  return (
    <div>
      {responseCount === 0
        ? <Alert type="info">No responses yet — all questions can be freely edited.</Alert>
        : <Alert type="warning">{responseCount} response{responseCount !== 1 ? 's' : ''} collected. Question type and scale are locked where answers exist.</Alert>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 16 }}>
        {questions.map((q, i) => {
          const locked = q.answer_count > 0;
          return (
            <div key={q.id || `new-${i}`} className="card card-elevated" style={{ padding: 16 }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: 'var(--text-muted)' }}>Q{i + 1}</span>
                    {locked && (
                      <span className="badge badge-sponsored" title="Wording and order can change; type cannot.">
                        <Lock size={10} /> {q.answer_count} answered
                      </span>
                    )}
                  </div>

                  <input className="form-input" value={q.question_text}
                    onChange={e => update(i, 'question_text', e.target.value)}
                    placeholder="Question text" style={{ width: '100%', marginBottom: 10 }} />

                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                    <select className="form-input" value={q.question_type} disabled={locked}
                      onChange={e => update(i, 'question_type', e.target.value)}
                      style={{ fontSize: '0.82rem', padding: '7px 12px' }}>
                      <option value="rating">Rating</option>
                      <option value="text">Free text</option>
                      <option value="multiple_choice">Multiple choice</option>
                    </select>

                    {q.question_type === 'rating' && (
                      <select className="form-input" value={q.render_style || 'stars'} disabled={locked}
                        onChange={e => update(i, 'render_style', e.target.value)}
                        style={{ fontSize: '0.82rem', padding: '7px 12px' }}>
                        <option value="stars">Stars (1–5)</option>
                        <option value="likert">Likert agreement</option>
                      </select>
                    )}

                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.82rem', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                      <input type="checkbox" checked={!!q.is_required}
                        onChange={e => update(i, 'is_required', e.target.checked)} />
                      Required
                    </label>
                  </div>

                  {q.question_type === 'multiple_choice' && (
                    <div style={{ marginTop: 12, paddingLeft: 14, borderLeft: 'var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {opts(q).map((o, oi) => (
                        <div key={oi} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <input className="form-input" value={o} onChange={e => setOpt(i, oi, e.target.value)}
                            placeholder={`Option ${oi + 1}`} style={{ flex: 1, fontSize: '0.85rem', padding: '7px 12px' }} />
                          <button className="btn btn-ghost btn-sm" onClick={() => rmOpt(i, oi)}><X size={12} /></button>
                        </div>
                      ))}
                      <button className="btn btn-secondary btn-sm" onClick={() => addOpt(i)} style={{ alignSelf: 'flex-start' }}>
                        <Plus size={12} /> Add option
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp size={13} /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => move(i, 1)} disabled={i === questions.length - 1}><ArrowDown size={13} /></button>
                  <button className="btn btn-ghost btn-sm" onClick={() => remove(i)}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <button className="btn btn-secondary" onClick={add} style={{ width: '100%', marginTop: 14 }}>
        <Plus size={14} /> Add question
      </button>
    </div>
  );
};

// ── Response viewer ─────────────────────────────────────────
const ResponseViewer = ({ formId, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try { const r = await feedbackAdminAPI.getResponses(formId); setData(r.data); }
      catch { toast.error('Failed to load responses.'); }
      finally { setLoading(false); }
    })();
  }, [formId]);

  const exportCSV = async () => {
    try {
      const r = await feedbackAdminAPI.exportCSV(formId);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `futurum_feedback_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded.');
    } catch { toast.error('Export failed.'); }
  };

  if (loading) return <LoadingPage />;
  if (!data) return null;

  return (
    <div>
      <PageHeader
        eyebrow="Feedback"
        title={data.form?.title || 'Responses'}
        subtitle={`${data.total_responses} response${data.total_responses !== 1 ? 's' : ''} received`}
        actions={
          <>
            <button className="btn btn-ghost btn-sm" onClick={onBack}><ArrowLeft size={14} /> Back</button>
            <button className="btn btn-primary btn-sm" onClick={exportCSV}><Download size={14} /> Download CSV</button>
          </>
        }
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {data.questions?.map(q => {
          const agg = data.aggregates?.[q.id];
          if (!agg) return null;

          return (
            <div key={q.id} className="card">
              <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.05rem', margin: '0 0 14px' }}>{q.question_text}</h3>

              {q.question_type === 'rating' && q.render_style === 'stars' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                    <div style={{ display: 'flex', gap: 2 }}>
                      {[1, 2, 3, 4, 5].map(s => (
                        <Star key={s} size={17}
                          fill={s <= Math.round(agg.average) ? 'var(--gold)' : 'transparent'}
                          stroke={s <= Math.round(agg.average) ? 'var(--gold)' : 'rgba(128,128,128,0.35)'} />
                      ))}
                    </div>
                    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)', fontSize: '1.05rem' }}>{agg.average.toFixed(2)}</span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>from {agg.count} rating{agg.count !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {[5, 4, 3, 2, 1].map(v => {
                      const c = agg.distribution?.[v] || 0;
                      const pct = agg.count > 0 ? (c / agg.count) * 100 : 0;
                      return (
                        <div key={v} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.78rem' }}>
                          <span style={{ width: 14, textAlign: 'right', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{v}</span>
                          <div style={{ flex: 1, height: 7, borderRadius: 'var(--radius-full)', background: 'rgba(128,128,128,0.12)', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${pct}%`, background: 'var(--gold)', borderRadius: 'var(--radius-full)' }} />
                          </div>
                          <span style={{ width: 28, textAlign: 'right', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>{c}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {q.question_type === 'rating' && q.render_style === 'likert' && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
                    <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--gold)', fontSize: '1.05rem' }}>{agg.average.toFixed(2)}</span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>from {agg.count} response{agg.count !== 1 ? 's' : ''}</span>
                  </div>
                  <div style={{ display: 'flex', height: 26, borderRadius: 'var(--radius-full)', overflow: 'hidden', background: 'rgba(128,128,128,0.1)', marginBottom: 12 }}>
                    {[1, 2, 3, 4, 5].map(v => {
                      const c = agg.distribution?.[v] || 0;
                      const pct = agg.count > 0 ? (c / agg.count) * 100 : 0;
                      if (!pct) return null;
                      return (
                        <div key={v} title={`${LIKERT[v]}: ${c} (${Math.round(pct)}%)`}
                          style={{
                            width: `${pct}%`, background: LIKERT_COLOR[v], display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            fontSize: '0.7rem', fontWeight: 600, color: '#0a0c14', fontFamily: 'var(--font-mono)',
                          }}>
                          {pct >= 9 ? `${Math.round(pct)}%` : ''}
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                    {[1, 2, 3, 4, 5].map(v => (
                      <span key={v} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: LIKERT_COLOR[v] }} />
                        {LIKERT[v]} · {agg.distribution?.[v] || 0}
                      </span>
                    ))}
                  </div>
                  {agg.count > 0 && (
                    <p style={{ marginTop: 14, marginBottom: 0, color: 'var(--teal)', fontSize: '0.85rem', fontWeight: 500 }}>
                      {Math.round((((agg.distribution?.[4] || 0) + (agg.distribution?.[5] || 0)) / agg.count) * 100)}% agree or strongly agree
                    </p>
                  )}
                </div>
              )}

              {q.question_type === 'multiple_choice' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {Object.entries(agg.counts || {}).map(([label, c]) => {
                    const pct = agg.total > 0 ? (c / agg.total) * 100 : 0;
                    return (
                      <div key={label}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 5 }}>
                          <span>{label}</span>
                          <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: '0.75rem' }}>{c} · {Math.round(pct)}%</span>
                        </div>
                        <div style={{ height: 7, borderRadius: 'var(--radius-full)', background: 'rgba(128,128,128,0.12)', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${pct}%`, background: 'var(--teal-dim)', borderRadius: 'var(--radius-full)' }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {q.question_type === 'text' && (
                <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {(agg.texts || []).length === 0
                    ? <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', margin: 0 }}>No written responses.</p>
                    : agg.texts.map((t, i) => (
                      <div key={i} style={{
                        fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6,
                        padding: '10px 14px', borderRadius: 'var(--radius-md)',
                        background: 'rgba(128,128,128,0.06)', borderLeft: '2px solid var(--teal-dim)',
                      }}>
                        {t.text}
                      </div>
                    ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main page ───────────────────────────────────────────────
export default function FeedbackAdminPage() {
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState([]);
  const [view, setView] = useState('list'); // list | edit | responses
  const [activeId, setActiveId] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);

  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState([]);
  const [responseCount, setResponseCount] = useState(0);
  const [originalIds, setOriginalIds] = useState([]);

  const load = useCallback(async () => {
    try { const r = await feedbackAdminAPI.getAll(); setForms(r.data || []); }
    catch { toast.error('Failed to load feedback forms.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setIsNew(true); setActiveId(null); setTitle(''); setQuestions([]);
    setResponseCount(0); setOriginalIds([]); setView('edit');
  };

  const openEdit = async (form) => {
    setIsNew(false); setActiveId(form.id); setEditLoading(true); setView('edit');
    try {
      const r = await feedbackAdminAPI.getDetail(form.id);
      setTitle(r.data.title);
      setQuestions(r.data.questions || []);
      setResponseCount(r.data.response_count || 0);
      setOriginalIds((r.data.questions || []).map(q => q.id));
    } catch {
      toast.error('Failed to load form.');
      setView('list');
    } finally { setEditLoading(false); }
  };

  const save = async () => {
    if (!title.trim()) { toast.error('A form title is required.'); return; }
    setSaving(true);
    const payload = {
      title: title.trim(),
      questions: questions.map((q, i) => ({
        id: q.id || undefined,
        question_text: q.question_text,
        question_type: q.question_type,
        render_style: q.render_style || 'stars',
        options: q.question_type === 'multiple_choice'
          ? (typeof q.options === 'string' ? JSON.parse(q.options) : q.options)
          : null,
        display_order: i,
        is_required: !!q.is_required,
      })),
    };

    const keptIds = questions.filter(q => q.id).map(q => q.id);
    const needsForce = originalIds.some(id => !keptIds.includes(id));

    try {
      if (isNew) {
        await feedbackAdminAPI.create(payload);
        toast.success('Feedback form created as draft.');
      } else {
        await feedbackAdminAPI.update(activeId, payload, needsForce);
        toast.success('Form updated.');
      }
      setView('list'); load();
    } catch (err) {
      const d = err.response?.data;
      if (d?.code === 'QUESTION_LOCKED') toast.error(d.message, { duration: 7000 });
      else toast.error(d?.error || 'Failed to save form.');
    } finally { setSaving(false); }
  };

  const setStatus = async (form, status) => {
    const verb = status === 'active' ? 'Open' : 'Close';
    if (!window.confirm(`${verb} feedback for "${form.title}"?`)) return;
    try {
      await feedbackAdminAPI.updateStatus(form.id, { status });
      toast.success(status === 'active' ? 'Feedback form is now live.' : 'Feedback closed.');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to update status.'); }
  };

  const remove = async (form) => {
    if (!window.confirm(`Delete draft form "${form.title}"?`)) return;
    try {
      await feedbackAdminAPI.remove(form.id);
      toast.success('Form deleted.'); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete form.'); }
  };

  if (loading) return <LoadingPage />;

  if (view === 'responses') {
    return <ResponseViewer formId={activeId} onBack={() => { setView('list'); load(); }} />;
  }

  if (view === 'edit') {
    const form = forms.find(f => f.id === activeId);
    return (
      <div>
        <PageHeader
          eyebrow="Feedback"
          title={isNew ? 'New Feedback Form' : 'Edit Questions'}
          subtitle={isNew ? 'Saved as a draft — activate it when you are ready' : form?.title}
          actions={
            <>
              <button className="btn btn-ghost btn-sm" onClick={() => setView('list')}><ArrowLeft size={14} /> Back</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving || editLoading}>
                {saving ? 'Saving…' : isNew ? 'Create Form' : 'Save Changes'}
              </button>
            </>
          }
        />

        {editLoading ? <LoadingPage /> : (
          <>
            {form?.status === 'active' && (
              <div style={{ marginBottom: 18 }}>
                <Alert type="warning">
                  <AlertTriangle size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                  This form is live. Changes take effect immediately for anyone who has not yet submitted.
                </Alert>
              </div>
            )}

            <div className="card" style={{ marginBottom: 18 }}>
              <FormGroup label="Form title" required>
                <input className="form-input" value={title} onChange={e => setTitle(e.target.value)}
                  placeholder="e.g. Season II Grand Finale — Your Feedback" style={{ width: '100%' }} />
              </FormGroup>
            </div>

            <QuestionBuilder questions={questions} setQuestions={setQuestions} responseCount={responseCount} />
          </>
        )}
      </div>
    );
  }

  const activeForm = forms.find(f => f.status === 'active');
  const totalResponses = forms.reduce((s, f) => s + Number(f.response_count || 0), 0);

  return (
    <div>
      <PageHeader
        eyebrow="Grand Finale"
        title="Feedback"
        subtitle="Audience feedback forms · guests respond at futurumawards.com/feedback"
        actions={
          <>
            <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={14} /> Refresh</button>
            <button className="btn btn-primary btn-sm" onClick={openNew}><Plus size={14} /> Create Form</button>
          </>
        }
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 24 }}>
        <StatCard label="Forms" value={forms.length} icon={FileText} color="teal" />
        <StatCard label="Currently Live" value={activeForm ? 1 : 0} icon={MessageSquare} color="green"
          sub={activeForm ? activeForm.title : 'None active'} />
        <StatCard label="Total Responses" value={totalResponses} icon={Users} color="gold" />
      </div>

      {forms.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={MessageSquare}
            title="No feedback forms"
            description="Create a form to gather audience feedback after the finale."
            action={<button className="btn btn-primary btn-sm" onClick={openNew}><Plus size={14} /> Create Form</button>}
          />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {forms.map(form => {
            const responses = Number(form.response_count || 0);
            return (
              <div key={form.id} className={`card ${form.status === 'active' ? 'card-teal' : ''}`}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, flexWrap: 'wrap' }}>
                      <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', margin: 0 }}>{form.title}</h3>
                      <span className={`badge badge-${form.status}`}>
                        {form.status === 'active' ? 'Live' : form.status}
                      </span>
                    </div>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', margin: 0, fontFamily: 'var(--font-mono)' }}>
                      {form.question_count} question{form.question_count !== 1 ? 's' : ''} · {responses} response{responses !== 1 ? 's' : ''}
                    </p>
                  </div>

                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                    {responses > 0 && (
                      <button className="btn btn-secondary btn-sm" onClick={() => { setActiveId(form.id); setView('responses'); }}>
                        <Eye size={13} /> Responses
                      </button>
                    )}
                    <button className="btn btn-ghost btn-sm" onClick={() => openEdit(form)}>
                      <Edit3 size={13} /> Edit
                    </button>
                    {form.status === 'draft' && (
                      <>
                        <button className="btn btn-teal btn-sm" onClick={() => setStatus(form, 'active')}>
                          <Play size={13} /> Go Live
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => remove(form)}><Trash2 size={13} /></button>
                      </>
                    )}
                    {form.status === 'active' && (
                      <button className="btn btn-danger btn-sm" onClick={() => setStatus(form, 'closed')}>
                        <Square size={13} /> Close
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
